/**
 * POI Taxonomies Store - Pinia
 *
 * Manages POI taxonomy definitions used for multi-select classification.
 */

import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';

function normalizeTitle (value) {
  return String(value || '').trim();
}

function normalizeOptionalString (value) {
  if (value === null || value === undefined) return null;
  const next = String(value).trim();
  return next || null;
}

function normalizeOptionalColorId (value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return Math.round(numeric);
}

function normalizeSortOrder (value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.round(numeric));
}

function normalizeIdArray (value) {
  if (!Array.isArray(value)) return [];

  const ids = value
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));

  return [...new Set(ids)];
}

function findById (rows, id) {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) return null;
  return rows.find((row) => Number(row?.id) === numericId) || null;
}

function sortTaxonomies (rows) {
  return [...rows].sort((a, b) => {
    const aSort = normalizeSortOrder(a?.sortOrder, 0);
    const bSort = normalizeSortOrder(b?.sortOrder, 0);
    if (aSort !== bSort) return aSort - bSort;

    const aTitle = String(a?.title || '').toLowerCase();
    const bTitle = String(b?.title || '').toLowerCase();

    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

async function removeTaxonomyFromPois (taxonomyId) {
  const numericTaxonomyId = Number(taxonomyId);
  if (!Number.isFinite(numericTaxonomyId)) return 0;

  const pois = await GeoMoverDB.findAll('pois');
  const touched = Array.isArray(pois)
    ? pois.filter((row) => normalizeIdArray(row?.taxonomyIds).includes(numericTaxonomyId))
    : [];

  for (const row of touched) {
    const nextTaxonomyIds = normalizeIdArray(row.taxonomyIds).filter((id) => id !== numericTaxonomyId);
    const now = Date.now();

    await GeoMoverDB.updateOne('pois', {
      ...row,
      taxonomyIds: nextTaxonomyIds,
      updatedAt: now,
      version: Math.max(1, Number(row?.version || 1) + 1),
      versionUpdatedAt: now,
      versionUpdatedByContext: 'taxonomy:remove'
    }, Number(row.id));
  }

  return touched.length;
}

export const usePoiTaxonomiesStore = defineStore('poiTaxonomies', {
  state: () => ({
    poiTaxonomies: [],
    isLoaded: false
  }),

  getters: {
    getAllPoiTaxonomies: (state) => state.poiTaxonomies,
    getPoiTaxonomyById: (state) => (id) => findById(state.poiTaxonomies, id)
  },

  actions: {
    async loadPoiTaxonomies () {
      try {
        const rows = await GeoMoverDB.findAll('poiTaxonomies');
        this.poiTaxonomies = sortTaxonomies(Array.isArray(rows) ? rows : []);
        this.isLoaded = true;
        return this.poiTaxonomies;
      }
      catch (error) {
        appDebugLog.error('PoiTaxonomiesStore', 'Error loading POI taxonomies:', error);
        this.poiTaxonomies = [];
        this.isLoaded = false;
        return [];
      }
    },

    async createPoiTaxonomy (input = {}) {
      const title = normalizeTitle(input.title);
      if (!title) {
        throw new Error('POI taxonomy title is required.');
      }

      if (!this.isLoaded) {
        await this.loadPoiTaxonomies();
      }

      const duplicate = this.poiTaxonomies.find((row) => String(row?.title || '').toLowerCase() === title.toLowerCase());
      if (duplicate) {
        throw new Error('POI taxonomy title must be unique.');
      }

      const now = Date.now();
      const payload = {
        title,
        colorId: normalizeOptionalColorId(input.colorId),
        colorKey: normalizeOptionalString(input.colorKey),
        iconKey: normalizeOptionalString(input.iconKey),
        sortOrder: normalizeSortOrder(input.sortOrder, (this.poiTaxonomies.length + 1) * 10),
        createdAt: now,
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('poiTaxonomies', payload);
      const persisted = { ...payload, id };
      this.poiTaxonomies = sortTaxonomies([...this.poiTaxonomies, persisted]);
      this.isLoaded = true;

      return persisted;
    },

    async updatePoiTaxonomy (taxonomyId, updates = {}) {
      const numericId = Number(taxonomyId);
      if (!Number.isFinite(numericId)) {
        throw new Error('Valid POI taxonomy ID is required.');
      }

      const current = this.getPoiTaxonomyById(numericId) || await GeoMoverDB.findOne('poiTaxonomies', numericId);
      if (!current) {
        throw new Error('POI taxonomy not found.');
      }

      const nextTitle = updates.title === undefined ? current.title : normalizeTitle(updates.title);
      if (!nextTitle) {
        throw new Error('POI taxonomy title is required.');
      }

      if (!this.isLoaded) {
        await this.loadPoiTaxonomies();
      }

      const duplicate = this.poiTaxonomies.find((row) => (
        Number(row?.id) !== numericId
        && String(row?.title || '').toLowerCase() === nextTitle.toLowerCase()
      ));
      if (duplicate) {
        throw new Error('POI taxonomy title must be unique.');
      }

      const next = {
        ...current,
        title: nextTitle,
        colorId: updates.colorId === undefined ? normalizeOptionalColorId(current.colorId) : normalizeOptionalColorId(updates.colorId),
        colorKey: updates.colorKey === undefined ? current.colorKey : normalizeOptionalString(updates.colorKey),
        iconKey: updates.iconKey === undefined ? current.iconKey : normalizeOptionalString(updates.iconKey),
        sortOrder: updates.sortOrder === undefined
          ? normalizeSortOrder(current.sortOrder, 0)
          : normalizeSortOrder(updates.sortOrder, 0),
        updatedAt: Date.now()
      };

      await GeoMoverDB.updateOne('poiTaxonomies', next, numericId);
      this.poiTaxonomies = sortTaxonomies([
        ...this.poiTaxonomies.filter((row) => Number(row?.id) !== numericId),
        next
      ]);
      this.isLoaded = true;

      return next;
    },

    async deletePoiTaxonomy (taxonomyId, options = {}) {
      const numericId = Number(taxonomyId);
      if (!Number.isFinite(numericId)) {
        throw new Error('Valid POI taxonomy ID is required.');
      }

      const existing = this.getPoiTaxonomyById(numericId) || await GeoMoverDB.findOne('poiTaxonomies', numericId);
      if (!existing) {
        return false;
      }

      if (options.cleanupPois !== false) {
        const touched = await removeTaxonomyFromPois(numericId);
        if (touched > 0) {
          appDebugLog.info('PoiTaxonomiesStore', `Removed taxonomy ${numericId} from ${touched} POI(s).`);
        }
      }

      await GeoMoverDB.deleteOne('poiTaxonomies', numericId);
      this.poiTaxonomies = sortTaxonomies(this.poiTaxonomies.filter((row) => Number(row?.id) !== numericId));
      this.isLoaded = true;
      return true;
    },

    async reorderPoiTaxonomies (orderedIds = []) {
      if (!this.isLoaded) {
        await this.loadPoiTaxonomies();
      }

      const nextIds = orderedIds
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

      if (nextIds.length !== this.poiTaxonomies.length) {
        throw new Error('Taxonomy reorder payload is incomplete.');
      }

      const currentIds = new Set(this.poiTaxonomies.map((taxonomy) => Number(taxonomy?.id)));
      if (nextIds.some((id) => !currentIds.has(id))) {
        throw new Error('Taxonomy reorder payload contains unknown IDs.');
      }

      const now = Date.now();
      const nextRows = nextIds.map((id, index) => {
        const current = this.getPoiTaxonomyById(id);
        return {
          ...current,
          sortOrder: (index + 1) * 10,
          updatedAt: now
        };
      });

      for (const row of nextRows) {
        await GeoMoverDB.updateOne('poiTaxonomies', row, Number(row.id));
      }

      this.poiTaxonomies = sortTaxonomies(nextRows);
      this.isLoaded = true;
      return this.poiTaxonomies;
    }
  }
});
